"use client";

import { Format, getFormatDisplayName } from '../../lib/formats';
import { cn } from '../../lib/utils';
import { CodeBlock, CodeBlockLine, SupernovaCodeBlock } from '@/components/ui';

interface HowToViewProps {
  format: Format;
  className?: string;
}

interface ViewComponentProps {
  format: Format;
}

function PandasView({ format }: ViewComponentProps) {
  return (
    <div className="prose prose-gray dark:prose-invert">
      <p>First, we need to install pandas</p>
      <SupernovaCodeBlock>
        <CodeBlockLine>{"pip install pandas"}</CodeBlockLine>
      </SupernovaCodeBlock>
      <p>{`Then we can load and view the ${format.toLowerCase()} file`}</p>
      <SupernovaCodeBlock>
        <CodeBlockLine>{`import pandas as pd`}</CodeBlockLine>
        {format === Format.CSV && <CodeBlockLine>{`df = pd.read_csv('path/to/file.csv')`}</CodeBlockLine>}
        {format === Format.TSV && <CodeBlockLine>{`df = pd.read_csv('path/to/file.tsv', sep='\\t')`}</CodeBlockLine>}
        {format === Format.PARQUET && <CodeBlockLine>{`df = pd.read_parquet('path/to/file.parquet')`}</CodeBlockLine>}
        {format === Format.XLSX && <CodeBlockLine>{`df = pd.read_excel('path/to/file.xlsx')`}</CodeBlockLine>}
        {format === Format.JSON && <CodeBlockLine>{`df = pd.read_json('path/to/file.json')`}</CodeBlockLine>}
        <CodeBlockLine>{`print(df.head())`}</CodeBlockLine>
      </SupernovaCodeBlock>
    </div>
  );
}

function DuckDBView({ format }: ViewComponentProps) {
  return (
    <div className="prose prose-gray dark:prose-invert">
      <p>First, we need to install duckdb</p>
      <SupernovaCodeBlock>
        <CodeBlockLine>{"pip install duckdb"}</CodeBlockLine>
      </SupernovaCodeBlock>
      <p>{`Then we can query the ${format.toLowerCase()} file directly`}</p>
      <SupernovaCodeBlock>
        <CodeBlockLine>{`import duckdb`}</CodeBlockLine>
        <CodeBlockLine>{`result = duckdb.sql(f"SELECT * FROM 'path/to/file.${format.toLowerCase()}' LIMIT 10").fetchdf()`}</CodeBlockLine>
        <CodeBlockLine>{`print(result)`}</CodeBlockLine>
      </SupernovaCodeBlock>
    </div>
  );
}

function ClickHouseView({ format }: ViewComponentProps) {
  return (
    <div className="prose prose-gray dark:prose-invert">
      <p>First, we need to install clickhouse-connect</p>
      <SupernovaCodeBlock>
        <CodeBlockLine>{"pip install clickhouse-connect"}</CodeBlockLine>
      </SupernovaCodeBlock>
      <p>{`Then we can load and query the ${format.toLowerCase()} file`}</p>
      <SupernovaCodeBlock>
        <CodeBlockLine>{`import clickhouse_connect`}</CodeBlockLine>
        <CodeBlockLine>{`client = clickhouse_connect.get_client(host='localhost', port=8123)`}</CodeBlockLine>
        <CodeBlockLine>{`query = f"SELECT * FROM file('path/to/file.${format.toLowerCase()}') LIMIT 10"`}</CodeBlockLine>
        <CodeBlockLine>{`result = client.query(query).result_set`}</CodeBlockLine>
        <CodeBlockLine>{`print(result)`}</CodeBlockLine>
      </SupernovaCodeBlock>
    </div>
  );
}

export function HowToView({ format, className }: HowToViewProps) {
  return (
    <div className={cn("space-y-6", className)}>
      <div className="prose prose-gray dark:prose-invert">
        <h2 className="text-2xl font-semibold tracking-tight mb-4">{`How to view ${getFormatDisplayName(format)} files in Python`}</h2>
        
        <p>
          Here are three effective ways to view {getFormatDisplayName(format)} files in Python using different libraries. Each approach has its own advantages depending on your specific needs and file sizes.
        </p>
        
        <h3 className="text-xl font-semibold tracking-tight mt-6">{`Viewing ${getFormatDisplayName(format)} files with Pandas`}</h3>
        <p>Pandas provides a straightforward approach for viewing files and works well for most common data tasks:</p>
        <PandasView format={format} />
        
        <h3 className="text-xl font-semibold tracking-tight mt-6">{`Viewing ${getFormatDisplayName(format)} files with DuckDB`}</h3>
        <p>DuckDB is an in-process SQL OLAP database that's perfect for larger files and analytical workloads:</p>
        <DuckDBView format={format} />
        
        <h3 className="text-xl font-semibold tracking-tight mt-6">{`Viewing ${getFormatDisplayName(format)} files with ClickHouse`}</h3>
        <p>ClickHouse is a high-performance column-oriented database system that's excellent for large-scale data processing:</p>
        <ClickHouseView format={format} />
      </div>
    </div>
  );
} 